import { useEffect } from "react";
import { useLockStore } from "../../store/lockStore";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";
import { HEARTBEAT_INTERVAL_MS } from "./constants";

/**
 * 3s heartbeat interval while we hold the lock. Two responsibilities beyond
 * just renewing the TTL on the server:
 *
 *  1. **Lock-lost detection.** If the response says `lockLost: true`, our
 *     session no longer owns the lock (someone else took over and the
 *     SignalR push didn't reach us). Transition straight to "removed".
 *  2. **Two-way `incomingRequest` reconciliation.** Authoritative server
 *     state catches missed SignalR pushes in both directions — a missed
 *     `accessRequested` populates `incomingRequest`; a missed `requestResolved`
 *     clears it. Without the else branch, the holder's banner would stick
 *     forever if a deny/cancel push was dropped during a reconnect.
 */
export function useLockHeartbeat(
  enabled: boolean,
  formId: string | null,
  sessionId: string,
  lockStatus: string,
): void {
  const setLockStatus = useLockStore((s) => s.setLockStatus);
  const setIncomingRequest = useLockStore((s) => s.setIncomingRequest);
  const setCountdown = useLockStore((s) => s.setCountdown);

  useEffect(() => {
    if (!enabled || !formId || lockStatus !== "locked") return;

    const id = window.setInterval(async () => {
      try {
        const result = await lockApi.heartbeat(formId, sessionId);
        if (result.lockLost) {
          // Pass through lockedBy so RemovedScreen can detect same-user takeover
          // (phone reclaimed from desktop via Path 4) and render its "Editing
          // Elsewhere" variant. Without lockedBy, the store sets it to null and
          // RemovedScreen falls back to "Another user is taking over the form"
          // — wrong for the same-user case AND missing the holder's email.
          setLockStatus("removed", result.lockedBy);
          return;
        }
        if (result.requestAccessBy) {
          const current = useLockStore.getState().incomingRequest;
          if (!current || current.email !== result.requestAccessBy) {
            setIncomingRequest({ email: result.requestAccessBy, since: Date.now() });
          }
        } else {
          const current = useLockStore.getState().incomingRequest;
          if (current) {
            setIncomingRequest(null);
            setCountdown(null);
          }
        }
      } catch (err) {
        eventBus.publish("error", { error: asError(err), context: "useLockHeartbeat" });
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [enabled, formId, sessionId, lockStatus, setLockStatus, setIncomingRequest, setCountdown]);
}
